import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  checkApi,
  type CheckQuestionCatalogue,
  type CheckTemplate,
  type CheckTemplateSummary,
  type CreateCheckTemplateRequest,
  type DuplicateCheckTemplateRequest,
  type UnitType,
  type UpdateCheckTemplateDraftRequest,
} from '../client';

/**
 * Stable query-key factories. Account scoping is implicit via the active
 * token; the explicit `organisationId` segment means switching org in the
 * AccountSwitcher always refetches.
 */
export const checkTemplateKeys = {
  all: ['check', 'templates'] as const,
  byOrganisation: (organisationId: string) =>
    ['check', 'templates', 'org', organisationId] as const,
  byId: (id: string) => ['check', 'templates', 'id', id] as const,
};

export const checkCatalogueKeys = {
  all: ['check', 'questionCatalogue'] as const,
  byUnit: (unit: UnitType | undefined, country: string | undefined) =>
    ['check', 'questionCatalogue', unit ?? 'all', country ?? 'all'] as const,
};

/**
 * The default question catalogue. Static reference data, identical for every
 * tenant and only changing on redeploy — cached for 24h, matching how the
 * Directory equipment and certification catalogues are treated.
 *
 * Pass a `unit` to get just that unit's questions (what the wizard needs to
 * say "starting with N default questions"); omit it for all 66.
 */
export function useCheckQuestionCatalogue(
  unit?: UnitType,
  country?: string,
  options?: Omit<UseQueryOptions<CheckQuestionCatalogue, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CheckQuestionCatalogue, Error>({
    queryKey: checkCatalogueKeys.byUnit(unit, country),
    queryFn: () => checkApi.getQuestionCatalogue(unit, country),
    staleTime: 24 * 60 * 60 * 1000,
    ...options,
  });
}

/** Every template in an organisation, newest-updated first (BE-ordered). */
export function useCheckTemplates(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<CheckTemplateSummary[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<CheckTemplateSummary[], Error>({
    queryKey: organisationId
      ? checkTemplateKeys.byOrganisation(organisationId)
      : checkTemplateKeys.all,
    queryFn: () => checkApi.getTemplates(organisationId!),
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * One template with the questions of its effective version — the draft when
 * one exists, otherwise the published version. That is what the editor loads.
 */
export function useCheckTemplate(id: string | null, organisationId: string | null) {
  return useQuery<CheckTemplate, Error>({
    queryKey: id ? checkTemplateKeys.byId(id) : checkTemplateKeys.all,
    queryFn: () => checkApi.getTemplate(id!, organisationId!),
    enabled: !!id && !!organisationId,
  });
}

/** Create a template. Returns the new id so the caller can open the editor. */
export function useCreateCheckTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateCheckTemplateRequest>({
    mutationFn: (body) => checkApi.createTemplate(body),
    onSuccess: (_id, body) => {
      qc.invalidateQueries({
        queryKey: checkTemplateKeys.byOrganisation(body.organisationId),
      });
    },
  });
}

/**
 * Replace the draft's name and question list.
 *
 * Invalidates the single-template cache as well as the list, because the BE
 * may have forked a NEW draft version to satisfy the write — the response id
 * is the template, not the version, so the editor has to re-read to learn
 * which version it is now editing.
 */
export function useUpdateCheckTemplateDraft() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateCheckTemplateDraftRequest>({
    mutationFn: (body) => checkApi.updateTemplateDraft(body),
    onSuccess: (_id, body) => {
      qc.invalidateQueries({ queryKey: checkTemplateKeys.byId(body.id) });
      qc.invalidateQueries({
        queryKey: checkTemplateKeys.byOrganisation(body.organisationId),
      });
    },
  });
}

/**
 * Publish the draft.
 *
 * Fails with a 400 when a Wac template omits a mandatory catalogue question;
 * the error message names them, so surface `error.message` rather than a
 * generic string — it is the actionable part.
 */
export function usePublishCheckTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, { id: string; organisationId: string }>({
    mutationFn: ({ id, organisationId }) => checkApi.publishTemplate(id, organisationId),
    onSuccess: (_versionId, { id, organisationId }) => {
      qc.invalidateQueries({ queryKey: checkTemplateKeys.byId(id) });
      qc.invalidateQueries({ queryKey: checkTemplateKeys.byOrganisation(organisationId) });
    },
  });
}

/** Duplicate a template. The copy is always an unpublished draft. */
export function useDuplicateCheckTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, DuplicateCheckTemplateRequest>({
    mutationFn: (body) => checkApi.duplicateTemplate(body),
    onSuccess: (_id, body) => {
      qc.invalidateQueries({
        queryKey: checkTemplateKeys.byOrganisation(body.organisationId),
      });
    },
  });
}

/**
 * Soft-delete a template. Rejected with a 409 once checks exist against it —
 * show `error.message`, which explains that and suggests archiving instead.
 */
export function useDeleteCheckTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, { id: string; organisationId: string }>({
    mutationFn: ({ id, organisationId }) => checkApi.deleteTemplate(id, organisationId),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({ queryKey: checkTemplateKeys.byOrganisation(organisationId) });
    },
  });
}
